import type { TradeHistoryItem } from '@/state/account'
import { type Order, OrderSide, OrderStatus } from '@/types/generated/common/trading'
import type { UnifiedOrderItem } from '@/types/generated/public_api'

/**
 * Home's "what happened" feed: fills, liquidations, cancels/rejects and newly placed orders
 * from every account, merged into one time-ordered list. Pure (tested in
 * __tests__/home-activity.test.ts); the atoms feeding it come from the trade layout's syncs.
 */

export type ActivityKind = 'fill' | 'liquidation' | 'placed' | 'cancelled' | 'rejected'

export interface ActivityItem {
  id: string
  kind: ActivityKind
  side: 'buy' | 'sell' | null
  symbol: string
  exchange: Order['exchange']
  accountId: string
  /** Filled size for a fill, order size otherwise (as the venue reported it). */
  quantity: string
  /** Fill price for a fill, limit price for an order; undefined for a market order. */
  price: string | undefined
  /** Epoch ms. */
  at: number
}

function sideOf(order: Order): ActivityItem['side'] {
  if (order.side === OrderSide.ORDER_SIDE_BUY) return 'buy'
  if (order.side === OrderSide.ORDER_SIDE_SELL) return 'sell'
  return null
}

function timeOf(value: string | undefined): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

function itemFrom(order: Order, kind: ActivityKind, at: number): ActivityItem {
  const isFill = kind === 'fill' || kind === 'liquidation'
  return {
    id: `${kind}:${order.accountId}:${order.orderId}:${at}`,
    kind,
    side: sideOf(order),
    symbol: order.symbol,
    exchange: order.exchange,
    accountId: order.accountId,
    quantity: (isFill ? order.filledQuantity : undefined) || order.quantity,
    price: (isFill ? order.avgFillPrice : undefined) || order.price || undefined,
    at,
  }
}

export interface ActivitySources {
  trades: readonly TradeHistoryItem[]
  orderHistory: readonly UnifiedOrderItem[]
  openOrders: readonly UnifiedOrderItem[]
}

export function buildActivityFeed(sources: ActivitySources, limit = 20): ActivityItem[] {
  const items = new Map<string, ActivityItem>()
  const add = (item: ActivityItem) => {
    if (item.at > 0 && !items.has(item.id)) items.set(item.id, item)
  }

  for (const trade of sources.trades) {
    const order = trade.order
    if (!order) continue
    const kind = order.isLiquidation || trade.liquidation ? 'liquidation' : 'fill'
    add(itemFrom(order, kind, timeOf(order.createdAt)))
  }
  // Filled orders are already in the feed as their fills; history adds what never filled.
  for (const { order } of sources.orderHistory) {
    if (!order) continue
    if (order.status === OrderStatus.ORDER_STATUS_CANCELLED) {
      add(itemFrom(order, 'cancelled', timeOf(order.updatedAt) || timeOf(order.createdAt)))
    } else if (order.status === OrderStatus.ORDER_STATUS_REJECTED) {
      add(itemFrom(order, 'rejected', timeOf(order.updatedAt) || timeOf(order.createdAt)))
    }
  }
  for (const { order } of sources.openOrders) {
    if (order) add(itemFrom(order, 'placed', timeOf(order.createdAt)))
  }

  return [...items.values()].sort((a, b) => b.at - a.at).slice(0, limit)
}

export type ActivityCounts = Record<ActivityKind, number>

/** How many of each kind happened after `since` (epoch ms). */
export function countActivitySince(items: readonly ActivityItem[], since: number): ActivityCounts {
  const counts: ActivityCounts = { fill: 0, liquidation: 0, placed: 0, cancelled: 0, rejected: 0 }
  for (const item of items) {
    if (item.at > since) counts[item.kind] += 1
  }
  return counts
}

/** Epoch ms of local midnight today, for "today" counts. */
export function startOfToday(now: number): number {
  const date = new Date(now)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}
